import React, { useState, memo } from 'react';
import { Box, Button, Typography, IconButton, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { AddCircle, Delete } from '@material-ui/icons';
import { DateTime } from 'luxon';
import { DatePicker } from '@material-ui/pickers';
import { AlertCard } from '../../cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

// Isolated DatePicker component to prevent state sharing
const IsolatedDatePicker = memo(
  ({
    value,
    onChange,
    minDate,
  }: {
    value: DateTime | null;
    onChange: (date: DateTime | null) => void;
    minDate?: DateTime;
  }) => {
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        format='dd/MM/yyyy'
        variant='inline'
        inputVariant='outlined'
        size='small'
        fullWidth
        minDate={minDate}
        autoOk
      />
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: '8px 0 20px 0',
    },
    header: {
      fontSize: '16px',
      fontWeight: 600,
      marginBottom: '8px',
      color: theme.color.grey.neutralBrand800,
    },
    description: {
      fontSize: '14px',
      color: theme.color.grey.neutral200,
      marginBottom: '24px',
      lineHeight: 1.5,
    },
    dateGroup: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
    },
    dateField: {
      flex: 1,
    },
    dateRangeLabel: {
      fontSize: '14px',
      fontWeight: 500,
      marginBottom: '8px',
      color: theme.color.grey.neutralBrand800,
    },
    deleteButton: {
      color: theme.color.error[900],
      padding: '8px',
    },
    addButton: {
      boxSizing: 'border-box',
      borderRadius: '4px',
      textTransform: 'none',
      color: '#ffffff',
      padding: '7px 12px',
      minWidth: '45px',
      backgroundColor: theme.color.secondary.main900,
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: '19px',
      marginTop: '8px',
      '&:hover': {
        backgroundColor: theme.color.secondary.main900,
        opacity: 0.9,
      },
    },
  })
);

export interface DateRange {
  id?: string;
  startDate: string | null;
  endDate: string | null;
}

export interface UnavailabilityRange {
  id?: string;
  startDate: string;
  endDate: string;
}

interface Props {
  onChange: (unavailabilities: UnavailabilityRange[]) => void;
  existingUnavailabilities?: UnavailabilityRange[];
}

export function BulkUnavailabilitySelect({ onChange, existingUnavailabilities = [] }: Props) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  // Track if user has cleared all unavailabilities
  const [clearedAll, setClearedAll] = useState(false);

  // Track if user has made any changes (to prevent overwriting from parent)
  const isDirty = React.useRef(false);

  // Initialize with existing unavailabilities or a single empty range
  const [dateRanges, setDateRanges] = useState<DateRange[]>(() => {
    if (existingUnavailabilities.length > 0) {
      return existingUnavailabilities.map((u, index) => ({
        id:
          u.id ||
          `range-${Date.now()}-${index}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        startDate: u.startDate,
        endDate: u.endDate,
      }));
    }
    return [
      {
        id: `range-${Date.now()}`,
        startDate: DateTime.local().toISODate(),
        endDate: null,
      },
    ];
  });

  // Update dateRanges when existingUnavailabilities changes ONLY on initial load
  // Don't update if user has already made changes (isDirty)
  const initializedRef = React.useRef(false);
  React.useEffect(() => {
    if (!initializedRef.current && existingUnavailabilities.length > 0) {
      const newRanges = existingUnavailabilities.map((u, index) => ({
        id:
          u.id ||
          `range-${Date.now()}-${index}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        startDate: u.startDate,
        endDate: u.endDate,
      }));
      setDateRanges(newRanges);
      setClearedAll(false);
      initializedRef.current = true;
    }
  }, [existingUnavailabilities]);

  const updateDates = (newRanges: DateRange[], cleared: boolean = false) => {
    setDateRanges(newRanges);
    setClearedAll(cleared);
    isDirty.current = true; // Mark as dirty when user makes changes

    // Convert to UnavailabilityRange format for the API
    const unavailabilities: UnavailabilityRange[] = newRanges
      .filter((range) => range.startDate)
      .map((range) => ({
        id: range.id?.startsWith('range-') ? undefined : range.id, // Only send ID if it's an existing one
        startDate: range.startDate!,
        endDate: range.endDate || range.startDate!,
      }));

    onChange(unavailabilities);
  };

  const handleStartDateChange = (rangeId: string | undefined, date: string | null) => {
    const newRanges = dateRanges.map((range) => (range.id === rangeId ? { ...range, startDate: date } : { ...range }));
    updateDates(newRanges);
  };

  const handleEndDateChange = (rangeId: string | undefined, date: string | null) => {
    const newRanges = dateRanges.map((range) => (range.id === rangeId ? { ...range, endDate: date } : { ...range }));
    updateDates(newRanges);
  };

  const addDateRange = () => {
    const newRange: DateRange = {
      id: `range-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      startDate: DateTime.local().toISODate(),
      endDate: null,
    };
    updateDates([...dateRanges, newRange]);
  };

  const removeDateRange = (rangeId: string | undefined) => {
    const newRanges = dateRanges.filter((range) => range.id !== rangeId);

    // If removing the last range and we had existing unavailabilities,
    // mark as cleared (will show message instead of empty inputs)
    if (newRanges.length === 0 && existingUnavailabilities.length > 0) {
      updateDates([], true);
    } else if (newRanges.length === 0) {
      // No existing unavailabilities, show empty input for new entry
      newRanges.push({
        id: `range-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        startDate: null,
        endDate: null,
      });
      updateDates(newRanges);
    } else {
      updateDates(newRanges);
    }
  };

  return (
    <Box className={classes.container}>
      {/* Only show info card when not in cleared state */}
      {!(clearedAll && dateRanges.length === 0) && (
        <AlertCard variant='info' title='Managing Your Unavailability'>
          <Typography style={{ fontSize: '14px', marginTop: '4px', lineHeight: 1.5, color: theme.alert.blue.medium }}>
            Setting unavailability will remove any existing availability for the selected dates across all your enrolled
            activities. This is recommended if you are going on leave, holidays, or can temporarily not attend rosters
            and activities. <br />
            You will be marked as unavailable for rostering during these periods.
            <br />
            <br />
            You can return to this dialog at any time to edit or remove your unavailability periods.
          </Typography>
        </AlertCard>
      )}

      {clearedAll && dateRanges.length === 0 ? (
        <Box marginTop='24px'>
          <AlertCard variant='warning' title='All Unavailabilities Will Be Cleared'>
            <Typography
              style={{ fontSize: '14px', marginTop: '4px', lineHeight: 1.5, color: theme.alert.yellow.medium }}
            >
              You have removed all unavailability periods. Submitting will clear all your existing unavailabilities and
              mark you as &apos;not indicated&apos; for rostering.
            </Typography>
          </AlertCard>
          <Button
            startIcon={<AddCircle style={{ fontSize: '18px' }} />}
            onClick={() => {
              setClearedAll(false);
              setDateRanges([
                {
                  id: `range-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                  startDate: DateTime.local().toISODate(),
                  endDate: null,
                },
              ]);
            }}
            className={classes.addButton}
            disableElevation
            style={{ marginTop: '16px' }}
          >
            Add New Unavailability Period
          </Button>
        </Box>
      ) : (
        <>
          <Box display='flex' marginBottom='16px' marginTop='24px'>
            <Box className={classes.dateField} marginRight='16px'>
              <Typography className={classes.dateRangeLabel}>Start Date</Typography>
            </Box>
            <Box className={classes.dateField}>
              <Typography className={classes.dateRangeLabel}>End Date</Typography>
            </Box>
            <Box width='48px' marginLeft='16px' />
          </Box>

          {dateRanges.map((range) => {
            const startValue = range.startDate ? DateTime.fromISO(range.startDate) : null;
            const endValue = range.endDate ? DateTime.fromISO(range.endDate) : null;
            const minEndDate = range.startDate ? DateTime.fromISO(range.startDate) : undefined;

            return (
              <Box key={range.id} className={classes.dateGroup}>
                <Box className={classes.dateField} marginRight='16px'>
                  <IsolatedDatePicker
                    key={`start-${range.id}`}
                    value={startValue}
                    onChange={(date) => handleStartDateChange(range.id, date?.toISODate() || null)}
                  />
                </Box>

                <Box className={classes.dateField}>
                  <IsolatedDatePicker
                    key={`end-${range.id}`}
                    value={endValue}
                    onChange={(date) => handleEndDateChange(range.id, date?.toISODate() || null)}
                    minDate={minEndDate}
                  />
                </Box>

                <IconButton
                  className={classes.deleteButton}
                  onClick={() => removeDateRange(range.id)}
                  size='small'
                  style={{
                    marginLeft: '16px',
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
            );
          })}

          <Button
            startIcon={<AddCircle style={{ fontSize: '18px' }} />}
            onClick={addDateRange}
            className={classes.addButton}
            disableElevation
            style={{ marginTop: '16px' }}
          >
            Add Another Date Range
          </Button>
        </>
      )}
    </Box>
  );
}
